import { View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import { Text } from "@shared/components/text";
import { colorPalette } from "@shared/config/colorsConfig";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { getProfessionalFullName } from "@shared/utils/doctor";
import { ActivityType, IPathwayActivitiesResponse, IPathwayActivity } from "app/_modules/pathways/entities";
import { TActivityBucket } from "../../../utils/activityCard";
import { mapToCardStatus, mapModalityToType, getMinutesUntilAppointment, APPOINTMENT_JOIN_WINDOW_MINUTES } from "../../../utils/appointment";
import { APPOINTMENT_CARD_STATUS, APPOINTMENT_STATUS } from "../../../constants/appointmentModality";
import { APPOINTMENT_TYPE, APPOINTMENT_MODALITY } from "../../../constants/appointmentModality";
import { ReminderCard } from "./ReminderCard";

const BUCKETS: TActivityBucket[] = ['daily', 'morning', 'afternoon', 'evening'];

const isAppointment = (activity: IPathwayActivity) =>
    activity.activityType === ActivityType.PATHWAY_APPOINTMENT;

const isAppointmentActive = (activity: IPathwayActivity) =>
    activity.appointment && activity.appointment.status !== APPOINTMENT_STATUS.CANCELLED;

const buildReminderViewModel = (activity: IPathwayActivity, t: (key: any, options?: any) => string) => {
    const appointment = activity.appointment;
    const isTelevisit = activity.activityName === APPOINTMENT_TYPE.TELEVISIT;
    const minutesUntil = appointment ? getMinutesUntilAppointment(appointment.date, appointment.time) : Infinity;
    const isWithinJoinWindow = minutesUntil <= APPOINTMENT_JOIN_WINDOW_MINUTES;
    const status = mapToCardStatus(appointment?.status, isWithinJoinWindow);
    const modalityType = mapModalityToType(appointment?.modality || '');
    const isRemoteVisit = isTelevisit || modalityType === APPOINTMENT_MODALITY.TELEASSISTANCE;
    const isConnectable = !!appointment && status === APPOINTMENT_CARD_STATUS.UPCOMING && isRemoteVisit;
    const isMoreThan15MinAway = appointment != null && minutesUntil > APPOINTMENT_JOIN_WINDOW_MINUTES;
    const showNote = appointment?.modality != APPOINTMENT_MODALITY.ON_SITE && appointment?.modality != APPOINTMENT_MODALITY.AT_HOME;
    const professional = appointment?.professional && {
        ...appointment.professional,
        fullName: getProfessionalFullName(appointment.professional),
    };
    const modalityLabel = isTelevisit
        ? t('appointments.card.modality.televisita')
        
        : t(`appointments.card.modality.${mapModalityToType(appointment?.modality || '')}`);
    const detail = professional?.fullName ? `${modalityLabel} · ${professional.fullName}` : modalityLabel;

    return {
        time: appointment?.time ? appointment.time.substring(0, 5) : '',
        title: appointment?.reason || t('screens.activities.reminders.visit'),
        detail,
        note: showNote && isMoreThan15MinAway ? t('screens.activities.reminders.activeBefore') : undefined,
        isConnectable,
        isRemoteVisit,
        navigateParams: isConnectable
            ? {
                appointmentId: appointment!.id,
                activityId: activity.activityId,
                scheduledTime: appointment!.time?.substring(0, 5),
                professional,
                appointmentType: isTelevisit ? APPOINTMENT_TYPE.TELEVISIT : APPOINTMENT_TYPE.TELEASSISTANCE,
            }
            : undefined,
    };
};

export const TodayReminders = ({ activities }: { activities: IPathwayActivitiesResponse }) => {
    const { t } = dynamicTranslations();
    const navigation = useNavigation<any>();

    const appointments = BUCKETS.flatMap((bucket) =>
        (activities[bucket] || []).filter(a => isAppointment(a) && isAppointmentActive(a))
    );

    if (appointments.length === 0) return null;

    return (
        <View className="flex-col gap-4">
            <View className="flex-row items-center gap-2">
                <Icon name="bell" size={18} color={colorPalette.black} />
                <Text className="font-semibold text-sm">{t('screens.activities.reminders.title')}</Text>
            </View>

            <View className="flex-col gap-3">
                {appointments.map((activity) => {
                    const reminder = buildReminderViewModel(activity, t);

                    return (
                        <ReminderCard
                            key={activity.activityId}
                            time={reminder.time}
                            title={reminder.title}
                            detail={reminder.detail}
                            detailIcon="video-camera"
                            note={reminder.note}
                            actionLabel={t('screens.activities.reminders.access')}
                            actionEnabled={reminder.isConnectable}
                            showAction={reminder.isRemoteVisit}
                            onPress={reminder.navigateParams ? () => navigation.navigate('AppointmentScreen', reminder.navigateParams) : undefined}
                        />
                    );
                })}
            </View>
        </View>
    );
};

export default TodayReminders;
